// connectorAnchorIndex — pure helper för att bygga den lookup-array +
// dimensions-record som ConnectorPath använder för att slå upp connector-
// endpoints. Endpoints kan vara Block-id ELLER Group-id (per type-kontrakt
// i workflow.ts:129-130). Denna helper exponerar pseudo-block-entries för
// båda group-typerna så ConnectorPath:s `blocks.find(b => b.id === fromId)`
// hittar dem oavsett om endpoint är Block, collapsed Group (Layer) eller
// expanded Group.

import type {
  WorkflowBlock,
  BlockGroup,
  BlockDimensions,
} from '@/types/workflow';
import { computeGroupBounds } from '@/lib/workflowCentering';
import { computeGroupCenter } from '@/hooks/useLayerNavigation';
import {
  COLLAPSED_CARD_WIDTH,
  COLLAPSED_CARD_HEIGHT_MIN,
} from '@/components/editor/CollapsedGroupCard';

export interface AnchorIndex {
  /** Block + pseudo-block-entries för varje group (collapsed eller expanded). */
  items: WorkflowBlock[];
  /** Dimensioner per id — innehåller även pseudo-entries för groups. */
  dims: Record<string, BlockDimensions>;
}

/** Bygg lookup-index för connector-endpoint-resolvering. */
export function buildAnchorIndex(args: {
  visibleBlocks: WorkflowBlock[];
  allBlocks: WorkflowBlock[];
  collapsedGroups: BlockGroup[];
  expandedGroups: BlockGroup[];
  blockDimensions: Record<string, BlockDimensions>;
}): AnchorIndex {
  const { visibleBlocks, allBlocks, collapsedGroups, expandedGroups, blockDimensions } = args;
  const items: WorkflowBlock[] = [...visibleBlocks];
  const dims: Record<string, BlockDimensions> = { ...blockDimensions };

  for (const group of collapsedGroups) {
    const center = computeGroupCenter(group, allBlocks);
    items.push({
      id: group.id,
      type: 'general',
      x: group.collapsedX ?? center.x,
      y: group.collapsedY ?? center.y,
      config: { name: group.name || 'Group' },
      color: group.color,
    });
    // Initial dim baseras på min-höjden — den faktiska höjden växer med
     // antal chips, vilket CollapsedGroupCard:s ResizeObserver matar
     // tillbaka via `onResize` → blockDimensions och då vinner det värdet
     // över denna fallback (`...blockDimensions` ovan har företräde
     // eftersom den spread:as efter loopen-input men innan utgångs-objektet
     // konstrueras... wait, actually this dims[group.id] line OVERWRITES
     // any blockDimensions entry — fix is to only set if not already in
     // blockDimensions, så ResizeObserver-värdet vinner.
    if (dims[group.id] == null) {
      dims[group.id] = { width: COLLAPSED_CARD_WIDTH, height: COLLAPSED_CARD_HEIGHT_MIN };
    }
  }

  for (const group of expandedGroups) {
    const bounds = computeGroupBounds(group.blockIds, allBlocks);
    if (!bounds) continue;
    items.push({
      id: group.id,
      type: 'general',
      x: (bounds.boundsLeft + bounds.boundsRight) / 2,
      y: (bounds.boundsTop + bounds.boundsBottom) / 2,
      config: { name: group.name || 'Group' },
      color: group.color,
    });
    dims[group.id] = {
      width: bounds.boundsRight - bounds.boundsLeft,
      height: bounds.boundsBottom - bounds.boundsTop,
    };
  }

  return { items, dims };
}
